import {inject, Injectable, InjectionToken, Injector} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {catchError, firstValueFrom, lastValueFrom, throwError} from 'rxjs';
import {DateTime} from 'luxon';
import {CookieOptions, CookieService} from 'ngx-cookie-service';
import {FeatureFlagService} from '@brunata-angular/util';

export async function isFeatureEnabled(
    injector: Injector,
    featureFlagId: string,
): Promise<boolean> {
    try {
        return await firstValueFrom(
            injector.get(FeatureFlagService).isFeatureEnabled$(featureFlagId),
        );
    } catch {
        return false;
    }
}

export type AuthConfig = {
    oauthClientId: string;
    loginRedirectUrl: string;
    interceptedUrls: string[];
    oauthAuthorizeUrl: string;
    oauthTokenUrl: string;
    scope: string;
    buildPasswordResetUrl?: () => string;
    treatServerErrorAsPasswordReset?: boolean;
};

export const AUTH_CONFIG = new InjectionToken<AuthConfig>('auth.config');

const cookieOptions: CookieOptions = {path: '/', secure: true, sameSite: 'Strict'};

// Browsers clamp persistent cookies to a maximum lifetime of 400 days, so this is the
// longest a "non-expiring" refresh token can actually be remembered in one go.
const MAX_COOKIE_LIFETIME_DAYS = 400;

function readOauthErrorCode(body: unknown): string | null {
    if (typeof body !== 'object' || body === null || !('error' in body)) {
        return null;
    }
    return typeof body.error === 'string' ? body.error : null;
}

// The message keeps the flattened body that makes a failed token request readable in Rollbar; the
// response is kept so callers can still classify on status and OAuth error code.
export class TokenRequestError extends Error {
    readonly oauthErrorCode: string | null;

    constructor(readonly response: HttpErrorResponse) {
        super(`${response.message}\n ${JSON.stringify(response.error, null, 4)}`);
        this.name = 'TokenRequestError';
        this.oauthErrorCode = readOauthErrorCode(response.error);
    }
}

// Session storage is per tab, so parallel logins in several tabs keep their own verifier and
// return URL.
const CODE_VERIFIER_KEY = 'code_verifier';
export const URL_BEFORE_AUTHORIZATION_KEY = 'url_before_authorization';
const EXPIRED_AUTHENTICATION_RETRIED_KEY = 'expired_authentication_retried';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(AUTH_CONFIG);
    private readonly cookieService = inject(CookieService);
    private readonly injector = inject(Injector);
    private authorizationInProgress = false;
    private inFlightRefresh: Promise<string | null> | null = null;

    async authorize(additionalParams?: Record<string, string>): Promise<void> {
        // Prevent multiple simultaneous authorization attempts
        if (this.authorizationInProgress) {
            return;
        }
        this.authorizationInProgress = true;

        const codeVerifier = this.generateCodeVerifier();
        sessionStorage.setItem(CODE_VERIFIER_KEY, codeVerifier);
        if (!window.location.href.includes(this.config.loginRedirectUrl)) {
            sessionStorage.setItem(URL_BEFORE_AUTHORIZATION_KEY, window.location.href);
        }

        const currentDomain = window.location.origin;
        const params = new URLSearchParams({
            /* eslint-disable @typescript-eslint/naming-convention */
            client_id: this.config.oauthClientId,
            redirect_uri: `${currentDomain}/${this.config.loginRedirectUrl}`,
            scope: this.config.scope,
            response_type: 'code',
            code_challenge: await this.getCodeChallenge(codeVerifier),
            code_challenge_method: 'S256',
            /* eslint-enable @typescript-eslint/naming-convention */
        });

        if (additionalParams !== undefined) {
            for (const [key, value] of Object.entries(additionalParams)) {
                params.set(key, value);
            }
        }

        document.location.replace(this.config.oauthAuthorizeUrl + '?' + params.toString());
    }

    async getAndStoreAccessTokenFromAuthRedirect(code: string): Promise<string> {
        const codeVerifier = sessionStorage.getItem(CODE_VERIFIER_KEY);
        if (codeVerifier === null) {
            throw new Error('Code verifier not found');
        }
        sessionStorage.removeItem(CODE_VERIFIER_KEY);
        const currentDomain = window.location.origin;
        const params = new URLSearchParams({
            /* eslint-disable @typescript-eslint/naming-convention */
            client_id: this.config.oauthClientId,
            redirect_uri: `${currentDomain}/${this.config.loginRedirectUrl}`,
            scope: this.config.scope,
            code: code,
            grant_type: 'authorization_code',
            code_verifier: codeVerifier,
            /* eslint-enable @typescript-eslint/naming-convention */
        });
        const accessToken = await this.getAndStoreAccessToken(params);
        sessionStorage.removeItem(EXPIRED_AUTHENTICATION_RETRIED_KEY);
        return accessToken;
    }

    // Keycloak answers authentication_expired when another tab of this browser finished the same
    // login first. The SSO session then exists, so one fresh authorization completes silently.
    canRetryExpiredAuthentication(error: string | null, errorDescription: string | null): boolean {
        return (
            error === 'temporarily_unavailable' &&
            errorDescription === 'authentication_expired' &&
            sessionStorage.getItem(EXPIRED_AUTHENTICATION_RETRIED_KEY) === null
        );
    }

    async retryExpiredAuthentication(additionalParams?: Record<string, string>): Promise<void> {
        sessionStorage.setItem(EXPIRED_AUTHENTICATION_RETRIED_KEY, 'true');
        await this.authorize(additionalParams);
    }

    refreshAccessToken(): Promise<string | null> {
        // Cold-start request bursts hit this concurrently. Share a single in-flight grant so we
        // never fire duplicate refresh POSTs.
        if (this.inFlightRefresh == null) {
            this.inFlightRefresh = this.performRefresh().finally(() => {
                this.inFlightRefresh = null;
            });
        }
        return this.inFlightRefresh;
    }

    private async performRefresh(): Promise<string | null> {
        if (
            !this.cookieService.check('refresh_token') ||
            !this.cookieService.check('refresh_token_expires')
        ) {
            return null;
        }
        const refreshToken = this.cookieService.get('refresh_token');
        const refreshTokenExpiresString = this.cookieService.get('refresh_token_expires');

        const refreshTokenExpires = DateTime.fromISO(refreshTokenExpiresString);
        if (refreshTokenExpires <= DateTime.now()) {
            // The refresh token itself has expired: the session is unrecoverable, so clear it.
            this.logOut();
            return null;
        }
        // No scope on the refresh grant: per RFC 6749 §6 an omitted scope reuses the scope
        // originally granted at login.
        const params = new URLSearchParams({
            /* eslint-disable @typescript-eslint/naming-convention */
            client_id: this.config.oauthClientId,
            refresh_token: refreshToken,
            grant_type: 'refresh_token',
            /* eslint-enable @typescript-eslint/naming-convention */
        });
        try {
            return await this.getAndStoreAccessToken(params);
        } catch (error) {
            if (!(error instanceof TokenRequestError)) {
                throw error;
            }
            if (error.oauthErrorCode === 'invalid_grant') {
                // Keycloak has retired the grant itself, so nothing is recoverable.
                this.logOut();
                return null;
            }
            // A dropped connection, timeout or 5xx says nothing about the grant. Not rethrown as the
            // HttpErrorResponse, so callers never read the token endpoint status as their own.
            throw error;
        }
    }

    private async getAndStoreAccessToken(params: URLSearchParams): Promise<string> {
        const options = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/x-www-form-urlencoded')
                .set('X-Skip-Interceptor', ''),
        };
        const response = await lastValueFrom(
            this.http
                .post<{
                    /* eslint-disable @typescript-eslint/naming-convention */
                    access_token: string;
                    refresh_token: string;
                    expires_in: number;
                    refresh_token_expires_in?: number;
                    refresh_expires_in?: number;
                    id_token?: string;
                    /* eslint-enable @typescript-eslint/naming-convention */
                }>(this.config.oauthTokenUrl, params, options)
                .pipe(
                    catchError((error: HttpErrorResponse) =>
                        throwError(() => new TokenRequestError(error)),
                    ),
                ),
        );

        const refreshTokenExpiresIn =
            response.refresh_token_expires_in ?? response.refresh_expires_in ?? 0;

        const refreshTokenExpires =
            refreshTokenExpiresIn > 0
                ? DateTime.now().plus({seconds: refreshTokenExpiresIn - 60})
                : DateTime.now().plus({days: MAX_COOKIE_LIFETIME_DAYS});

        // All cookies share the refresh-token lifetime: authGuard requires the access_token cookie
        // to be present before it will refresh, so letting it expire early would force a re-login
        // on restart even with a valid refresh token. The access token's real freshness is still
        // governed by access_token_expires below.
        const persistentCookieOptions: CookieOptions = {
            ...cookieOptions,
            expires: refreshTokenExpires.toJSDate(),
        };

        this.cookieService.set('access_token', response.access_token, persistentCookieOptions);
        this.cookieService.set('refresh_token', response.refresh_token, persistentCookieOptions);
        if (response.id_token != null) {
            this.cookieService.set('id_token', response.id_token, persistentCookieOptions);
        }
        // Refresh access token 60 seconds before it runs out just to be safe.
        this.cookieService.set(
            'access_token_expires',
            DateTime.now()
                .plus({seconds: response.expires_in - 60})
                .toISO(),
            persistentCookieOptions,
        );

        this.cookieService.set(
            'refresh_token_expires',
            refreshTokenExpires.toISO(),
            persistentCookieOptions,
        );

        return response.access_token;
    }

    private async getCodeChallenge(codeVerifier: string): Promise<string> {
        const encoder = new TextEncoder();
        const data = encoder.encode(codeVerifier);
        const hash = await crypto.subtle.digest('SHA-256', data);
        return btoa(String.fromCharCode(...new Uint8Array(hash)))
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=+$/, '');
    }

    private generateCodeVerifier(): string {
        const array = new Uint8Array(128 / 2);
        crypto.getRandomValues(array);
        return Array.from(array, dec => dec.toString(16)).join('');
    }

    logOut(): void {
        this.cookieService.delete('refresh_token', cookieOptions.path);
        this.cookieService.delete('refresh_token_expires', cookieOptions.path);
        this.cookieService.delete('access_token', cookieOptions.path);
        this.cookieService.delete('access_token_expires', cookieOptions.path);
        this.cookieService.delete('id_token', cookieOptions.path);
        this.cookieService.delete('token', cookieOptions.path);

        localStorage.removeItem('refresh_token');
        localStorage.removeItem('refresh_token_expires');
        localStorage.removeItem('access_token');
        localStorage.removeItem('access_token_expires');
    }
}
